import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { GraduationCapIcon, ShieldCheckIcon, MailIcon, ArrowRightIcon, CheckIcon, LockIcon, ZapIcon, ClipboardListIcon, SparklesIcon, SchoolIcon, AwardIcon } from 'lucide-react';
import { getBridgeTemplate } from '../data/bridgeTemplates';
import { useMetaTags } from '../hooks/useMetaTags';
import { V9BridgeLayout } from '../components/V9BridgeLayout';
import { TrackingNoticeLinks } from '../components/TrackingNoticeLinks';
import { getPublisherParams } from '../utils/publisherParams';
import { applyUrlPassthrough } from '../utils/urlPassthrough';
import { getWebLandingURL } from '../utils/landingUrl';
import { degreeLevelOptions, areaStudyOptions, programOptions, getDefaultProgram } from '../data/filterOptions';
const NAVY = '#1a2332';
const NAVY_ACCENT = '#2a374a';
const ORANGE = '#e8594f';
const ORANGE_HOVER = '#d64a41';
// Module-level lock so the email-remarketing beacon fires at most once per
// unique lead for the lifetime of the page session. A component-scoped ref
// resets on every remount (React StrictMode double-mount, route remounts,
// HMR), which caused the beacon to fire repeatedly. A module-level Set keyed
// by the request URL survives all remounts while still allowing a genuinely
// different lead (different URL) to fire its own single beacon.
const firedRemarketingUrls = new Set<string>();
export function FindDegreesBridgePage() {
  const {
    templateId
  } = useParams<{
    templateId: string;
  }>();
  const template = getBridgeTemplate(templateId);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // v6 selector state
  const [degreeLevel, setDegreeLevel] = useState<number | ''>('');
  const [areaOfStudy, setAreaOfStudy] = useState<number | ''>('');
  const [program, setProgram] = useState<number | ''>('');
  // v8 — Clicks Search carousel state. When `showClicksCarousel` is
  // true, the page swaps the selector form for an in-page carousel
  // that displays one school at a time from the Clicks API response.
  const [showClicksCarousel, setShowClicksCarousel] = useState(false);
  const [clicksResults, setClicksResults] = useState<any[]>([]);
  const [clicksLoading, setClicksLoading] = useState(false);
  const [clicksIndex, setClicksIndex] = useState(0);
  const [clicksError, setClicksError] = useState('');
  useMetaTags({
    title: `${template.headlineHighlight} | FindOnlineDegrees`,
    description: template.subhead,
    // Bridge templates are paid traffic landing pages — keep them out of
    // the organic index so they don't compete with content pages.
    robots: 'noindex, follow'
  });
  // Email remarketing beacon — fires once on landing whenever the URL
  // carries an email AND a first and/or last name. Forwards the lead to
  // Media Matchers' email-remarketing endpoint so the contact can be
  // enrolled in downstream nurture campaigns. Fire-and-forget (no UI).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const email = (params.get('email') || '').trim();
    const firstName = (params.get('firstName') || '').trim();
    const lastName = (params.get('lastName') || '').trim();
    // Require an email AND at least one name part before firing.
    if (!email || !firstName && !lastName) return;
    const remarketingParams = new URLSearchParams();
    // Publisher identification: prefer mPubGroupID, else mPubID+mPubCampaignID
    // from the URL; if all are blank fall back to the partner-supplied
    // mPubID/mPubCampaignID default pair.
    Object.entries(getPublisherParams(location.search, {
      mPubID: '83768',
      mPubCampaignID: '37138'
    })).forEach(([k, v]) => remarketingParams.set(k, v));
    // Map remaining values straight from the URL — only append the ones
    // that actually have a value so we don't send empty params.
    const passthrough: Record<string, string> = {
      firstName,
      lastName,
      email,
      sub1: params.get('sub1') || '',
      sub2: params.get('sub2') || '',
      sub3: params.get('sub3') || '',
      userIP: params.get('userIP') || '',
      zipCode: params.get('zipCode') || '',
      mProgram: params.get('mProgram') || '',
      startTimeframe: params.get('startTimeframe') || '',
      webLandingURL: getWebLandingURL()
    };
    Object.entries(passthrough).forEach(([k, v]) => {
      if (v) remarketingParams.set(k, v);
    });
    const url = `https://data.media-matchers.com/email-remarketing?${remarketingParams.toString()}`;
    // Guard against duplicate fires across remounts: only one beacon per
    // unique lead URL for the lifetime of this page session.
    if (firedRemarketingUrls.has(url)) return;
    firedRemarketingUrls.add(url);
    console.log('[Email Remarketing] PING (GET):', url);
    // Use default `cors` mode (not no-cors) so the response body is
    // readable. The request still reaches the server either way; cors mode
    // additionally exposes the JSON to JS/DevTools IF the endpoint returns
    // Access-Control-Allow-Origin. If it doesn't, the read is blocked
    // (the ping still lands) — open the logged URL in a browser tab to see
    // the raw JSON directly.
    fetch(url, {
      method: 'GET',
      keepalive: true
    }).then(async res => {
      const text = await res.text();
      try {
        const json = JSON.parse(text);
        console.log('[Email Remarketing] response JSON:', json);
      } catch {
        console.log('[Email Remarketing] response (raw):', text);
      }
    }).catch(err => {
      console.warn('[Email Remarketing] response not readable (likely CORS). The ping still reached the server. Open the PING URL in a new tab to see the JSON. Error:', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    params.set('email', email);
    navigate(`/?${params.toString()}`);
  };
  const handleCtaClick = () => {
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    // healthcare-v1: same single-button CTA layout as v2, but on click
    // swap the page for the in-page Clicks carousel with degreeInterest=5
    // (Master's) and mProgram=500 (Healthcare) hardcoded.
    if (templateId === 'healthcare-v1') {
      setDegreeLevel(5);
      setProgram(500);
      setShowClicksCarousel(true);
      callV8ClicksAPI(5, 500);
      return;
    }
    // v2: open the Media Matchers API as a pop-under and keep the user on
    // the listings page in the foreground tab.
    if (templateId === 'v2') {
      const externalParams = new URLSearchParams({
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || '',
        mediaType: '2',
        redirect: '1',
        webLandingURL: getWebLandingURL()
      });
      applyUrlPassthrough(externalParams, location.search);
      Object.entries(getPublisherParams(location.search, {
        mPubGroupID: 'PCGP4731148320'
      })).forEach(([k, v]) => externalParams.set(k, v));
      const externalUrl = `https://api.media-matchers.com/search?${externalParams.toString()}`;
      try {
        const popunder = window.open(externalUrl, '_blank');
        if (popunder) {
          popunder.blur();
          window.focus();
        }
      } catch {
        // Popup blocked or failed — proceed with main navigation regardless.
      }
      navigate(`/?${params.toString()}`);
      return;
    }
    // v5: navigate to the colleges page (preserve any sub params)
    if (templateId === 'v5') {
      navigate(`/colleges?${params.toString()}`);
      return;
    }
    // v3 only: redirect to findonlinedegrees.org request-information page
    if (templateId === 'v3') {
      const externalParams = new URLSearchParams({
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || ''
      });
      Object.entries(getPublisherParams(location.search, {
        mPubGroupID: 'PCGP4731148320'
      })).forEach(([k, v]) => externalParams.set(k, v));
      window.location.href = `https://findonlinedegrees.org/request-information?${externalParams.toString()}`;
      return;
    }
    navigate(`/?${params.toString()}`);
  };
  const isCta = template.variant === 'cta';
  const isSelector = template.variant === 'selector';
  const availablePrograms = areaOfStudy ? programOptions[areaOfStudy as number] || [] : [];
  // Fetch Clicks Search API and render results inline as carousel.
  // Used by both v8 (selector form) and healthcare-v1 (single CTA).
  // Accepts explicit overrides because state setters are async — the
  // caller may have just set state in the same tick.
  const callV8ClicksAPI = async (degreeInterestOverride?: number | '', mProgramOverride?: number | '') => {
    setClicksLoading(true);
    setClicksError('');
    setClicksResults([]);
    const di = degreeInterestOverride !== undefined ? degreeInterestOverride : degreeLevel;
    const mp = mProgramOverride !== undefined ? mProgramOverride : program;
    const params = new URLSearchParams(location.search);
    const searchParams = new URLSearchParams({
      degreeInterest: di === '' ? '' : String(di),
      mProgram: mp === '' ? '' : String(mp),
      sub1: params.get('sub1') || '',
      sub2: params.get('sub2') || '',
      sub3: params.get('sub3') || '',
      webLandingURL: getWebLandingURL()
    });
    applyUrlPassthrough(searchParams, location.search);
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => searchParams.set(k, v));
    const url = `https://api.media-matchers.com/search?${searchParams.toString()}`;
    console.log('[v8 Clicks API] PING (GET):', url);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: any = await response.json();
      console.log('[v8 Clicks API] response:', data);
      const schools = data?.result || data?.results || data?.schools || data?.data || (Array.isArray(data) ? data : []) || [];
      setClicksResults(Array.isArray(schools) ? schools : []);
      if (!schools || schools.length === 0) {
        setClicksError('No matching schools found.');
      }
    } catch (err) {
      clearTimeout(timeout);
      console.error('[v8 Clicks API] error:', err);
      setClicksError('Unable to load matching schools. Please try again.');
    } finally {
      setClicksLoading(false);
    }
  };
  const handleV8ClickThrough = () => {
    const school: any = clicksResults[clicksIndex];
    const clickURL = school?.clickURL || school?.clickUrl || school?.url || school?.link;
    if (clickURL) {
      try {
        window.open(clickURL, '_blank', 'noopener,noreferrer');
      } catch (err) {
        console.warn('[v8 Clicks] failed to open clickURL:', err);
      }
    }
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      const params = new URLSearchParams(location.search);
      params.set('degreeInterest', String(degreeLevel));
      params.set('mProgram', String(program));
      navigate(`/?${params.toString()}`);
    }
  };
  const handleV8Skip = () => {
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      const params = new URLSearchParams(location.search);
      params.set('degreeInterest', String(degreeLevel));
      params.set('mProgram', String(program));
      navigate(`/?${params.toString()}`);
    }
  };
  const handleSelectorSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!degreeLevel || !areaOfStudy || !program) return;
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    params.set('degreeInterest', String(degreeLevel));
    params.set('mProgram', String(program));
    // v8 and financial-aid-v1/v2: swap to in-page Clicks API carousel.
    if (templateId === 'v8' || templateId === 'financial-aid-v1' || templateId === 'financial-aid-v2') {
      setShowClicksCarousel(true);
      callV8ClicksAPI();
      return;
    }
    // v7: redirect to external Media Matchers search API,
    // and also open the home page (with the same selections) as a pop-under.
    if (templateId === 'v7') {
      const externalParams = new URLSearchParams({
        degreeInterest: String(degreeLevel),
        mProgram: String(program),
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || '',
        redirect: '1',
        webLandingURL: getWebLandingURL()
      });
      applyUrlPassthrough(externalParams, location.search);
      Object.entries(getPublisherParams(location.search, {
        mPubGroupID: 'PCGP4731148320'
      })).forEach(([k, v]) => externalParams.set(k, v));
      const externalUrl = `https://api.media-matchers.com/search?${externalParams.toString()}`;
      // Pop-under: open the API URL in a new tab and drop it behind,
      // then navigate the current window to the home/listings page so the
      // user stays on our site.
      try {
        const popunder = window.open(externalUrl, '_blank');
        if (popunder) {
          popunder.blur();
          window.focus();
        }
      } catch {
        // Popup blocked or failed — proceed with main navigation regardless.
      }
      navigate(`/?${params.toString()}`);
      return;
    }
    navigate(`/?${params.toString()}`);
  };
  // v9 layout override
  if (template.layout === 'hq') {
    return <V9BridgeLayout template={template} onCtaClick={handleCtaClick} submitting={submitting} data-id="element-1624" data-mp="pu2a7ctjfr4t" />;
  }
  // v8: when the carousel is active, render it in place of the
  // selector page. Keeps the slim header for brand consistency.
  if (showClicksCarousel) {
    const currentClick: any = clicksResults[clicksIndex];
    const cSchoolName = currentClick?.mBrandName || currentClick?.brandName || currentClick?.name || 'Partner School';
    const cSchoolLogo = currentClick?.imageURL || currentClick?.imageUrl || currentClick?.logoUrl;
    const cHeadline = currentClick?.headContent || '';
    const cSchoolHighlight = currentClick?.schoolHighlight || '';
    const cAboutText = currentClick?.aboutBrand || currentClick?.aboutCollege || currentClick?.shortDescription || '';
    const cAccreditation = currentClick?.accreditation || '';
    const cRichHtml = currentClick?.bodyContent || '';
    const cClickURL = currentClick?.clickURL || currentClick?.clickUrl || currentClick?.url || currentClick?.link;
    return <div className="min-h-screen flex flex-col font-sans text-gray-900 bg-gray-50" data-id="element-1625" data-mp="3uqdau9w9xy51">
        <header className="w-full relative overflow-hidden" style={{
        backgroundColor: NAVY
      }} data-id="element-1626" data-mp="da590vozb7kh">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1627" data-mp="8dy0cv577hyu"></div>
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between relative z-10" data-id="element-1628" data-mp="hca29tbim26l">
            <div className="flex items-center gap-2.5" data-id="element-1629" data-mp="2o411kxm5vli7">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
              backgroundColor: ORANGE
            }} data-id="element-1630" data-mp="2o4sv82w7795u">
                <GraduationCapIcon className="w-5 h-5 text-white" data-id="element-1631" data-mp="rztmdix0ziwh" />
              </div>
              <div className="leading-tight" data-id="element-1632" data-mp="s1o82zg4ff1m">
                <div className="font-bold text-white text-base" data-id="element-1633" data-mp="wyobf2ll63p1">
                  FindOnlineDegrees
                </div>
                <div className="text-[10px] text-gray-400 tracking-widest" data-id="element-1634" data-mp="1122w46kgz88t">
                  YOUR MATCHES
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-gray-300 text-xs" data-id="element-1635" data-mp="1ay5z1en5bawt">
              <LockIcon className="w-3.5 h-3.5 text-white" data-id="element-1636" data-mp="1fpfmq9p4w462" />
              <span data-id="element-1637" data-mp="1fq7gdez67rtp">SSL Secured</span>
            </div>
          </div>
        </header>
        <main className="flex-grow" data-id="element-1638" data-mp="1kn7d2za4ri85">
          <div className="max-w-3xl mx-auto px-4 py-12" data-id="element-1639" data-mp="1kqblk8ump04m">
            {clicksLoading ? <div className="text-center py-20" data-id="element-1640" data-mp="1oo11dtrhiz1k">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8594f] mb-4" data-id="element-1641" data-mp="1tq8gutbiwx8n"></div>
                <p className="text-xl text-gray-700 font-semibold" data-id="element-1642" data-mp="3e8srkdxpp2i5">
                  Finding your matches...
                </p>
              </div> : clicksError || clicksResults.length === 0 ? <div className="text-center py-20 bg-white rounded-xl border border-gray-200 shadow-sm" data-id="element-1643" data-mp="356t71bnhngag">
                <p className="text-xl text-gray-700 font-semibold mb-4" data-id="element-1644" data-mp="309aznpp4d94k">
                  {clicksError || 'No matches found.'}
                </p>
                <button onClick={() => {
              const params = new URLSearchParams(location.search);
              params.set('degreeInterest', String(degreeLevel));
              params.set('mProgram', String(program));
              navigate(`/?${params.toString()}`);
            }} className="px-6 py-3 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg transition-all" data-id="element-1645" data-mp="3svlz2x0zp3zs">
                  Continue to Listings →
                </button>
              </div> : <>
                <div className="text-center mb-8" data-id="element-1646" data-mp="3oyn9b8vy7lgk">
                  <p className="text-xs uppercase tracking-widest text-[#e8594f] font-bold mb-1" data-id="element-1647" data-mp="3ozg9fr0ejcpq">
                    Your Matches
                  </p>
                  <p className="text-sm text-gray-600 font-semibold" data-id="element-1648" data-mp="3f7k88730r6ha">
                    School {clicksIndex + 1} of {clicksResults.length}
                  </p>
                  <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden max-w-xs mx-auto" data-id="element-1649" data-mp="6qph1ovl3aj1">
                    <div className="h-full bg-[#e8594f] transition-all duration-300" style={{
                  width: `${(clicksIndex + 1) / clicksResults.length * 100}%`
                }} data-id="element-1650" data-mp="3tx89x9f4475j" />
                  </div>
                </div>
                <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-10" data-id="element-1651" data-mp="2mjeit710stb4">
                  {cSchoolHighlight && <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold border border-amber-200 shadow-sm" data-id="element-1652" data-mp="2eyaq23ww5ak3">
                      <span aria-hidden="true" data-id="element-1653" data-mp="2oxjr1gq6bpfy">✨</span>
                      {cSchoolHighlight}
                    </span>}
                  <div className="text-center mb-8" data-id="element-1654" data-mp="1w9e4qpjh1se0">
                    {cSchoolLogo && <img src={cSchoolLogo} alt={cSchoolName} className="h-28 md:h-32 mx-auto mb-6 object-contain" data-id="element-1655" data-mp="1rat5asc1ncqm" />}
                    {cAboutText && <p className="text-base text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-1656" data-mp="216nu656v241u">
                        {cAboutText}
                      </p>}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2" data-id="element-1657" data-mp="18hz02q482yzl">
                    {cSchoolName}
                  </h2>
                  {cHeadline && cHeadline.trim() !== cSchoolName.trim() && <p className="text-base md:text-lg text-[#e8594f] font-semibold text-center mb-3" data-id="element-1658" data-mp="2zk0tfwigj4e0">
                      {cHeadline}
                    </p>}
                  {cAccreditation && <p className="text-xs text-gray-500 text-center mb-5" data-id="element-1659" data-mp="39gm00ma4knjy">
                      Accredited by {cAccreditation}
                    </p>}
                  {cRichHtml && <div className="prose prose-sm max-w-none text-gray-700 mb-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1" dangerouslySetInnerHTML={{
                __html: cRichHtml
              }} data-id="element-1660" data-mp="3ih215p3ebiln" />}
                  <div className="space-y-4" data-id="element-1661" data-mp="1k4tooi8oiael">
                    <button onClick={handleV8ClickThrough} disabled={!cClickURL} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1662" data-mp="19yjzu2x45wzm">
                      Visit {cSchoolName} →
                    </button>
                    <button onClick={handleV8Skip} className="w-full py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg border-2 border-gray-300 transition-all" data-id="element-1663" data-mp="q99kiq1a5e21">
                      Skip This School
                    </button>
                  </div>
                </div>
              </>}
          </div>
        </main>
        {/* Always rendered: every page running the pixel needs its tracking
            notice and opt-out links (Meta Business Tools Terms §3.b). */}
        <footer className="w-full border-t border-gray-200 bg-white mt-auto" data-id="element-1664" data-mp="2e8u3cp0ewz38">
            <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-500" data-id="element-1665" data-mp="2a78hwf9tbchf">
              <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs mb-4 font-medium" data-id="element-1666" data-mp="257vxs2absjij">
                <a href="/ca-notice" className="hover:text-[#e8594f] transition-colors" data-id="element-1667" data-mp="2092lueaot0kh">
                  CA Consumers: Do Not Sell My Info
                </a>
                <a href="/terms" className="hover:text-[#e8594f] transition-colors" data-id="element-1668" data-mp="2sx832r2j87fk">
                  Terms
                </a>
                <a href="/privacy-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1669" data-mp="2j7y9t7vycipv">
                  Privacy Policy
                </a>
              </nav>
              <p className="text-xs mb-4" data-id="element-1670" data-mp="3bw2hfyy335v7">
                © {new Date().getFullYear()} FindOnlineDegrees. All rights
                reserved.
              </p>
              {template.footerDisclosure && <p className="text-[10px] leading-relaxed max-w-5xl mx-auto text-justify" data-id="element-1671" data-mp="3o7mrwyd2gwee">
                  {template.footerDisclosure}
                </p>}
              <div className="text-[10px] leading-relaxed max-w-3xl mx-auto pt-3" data-id="element-1672" data-mp="1zwnkro3nx6r">
                <TrackingNoticeLinks tone="light" data-id="element-1673" data-mp="60ynzbtr6fab" />
              </div>
            </div>
        </footer>
      </div>;
  }
  return <div className="min-h-screen flex flex-col font-sans text-gray-900 bg-gray-100 bg-cover bg-center bg-no-repeat bg-fixed" style={{
    backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/3azvozn9iRJDmqfhLE2fmB/Background_hero.png")'
  }} data-id="element-1674" data-mp="aza23yr2zbyc">
      {/* Slim Header — matches site nav style. Hidden on mobile to maximize
                                                above-the-fold form visibility. */}
      <header className="w-full relative overflow-hidden" style={{
      backgroundColor: NAVY
    }} data-id="element-1675" data-mp="togihf4ejrgz">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1676" data-mp="1mgutfqtq2fl9"></div>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between relative z-10" data-id="element-1677" data-mp="206y7vvmd1505">
          <div className="flex items-center gap-2.5" data-id="element-1678" data-mp="1w6xsa7niynri">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
            backgroundColor: ORANGE
          }} data-id="element-1679" data-mp="18fs331vp2j4j">
              <GraduationCapIcon className="w-5 h-5 text-white" data-id="element-1680" data-mp="1hhrl9xig3t92" />
            </div>
            <div className="leading-tight" data-id="element-1681" data-mp="1ci58tqafyh1b">
              <div className="font-bold text-white text-base" data-id="element-1682" data-mp="1chdnr9eh01pa">
                FindOnlineDegrees
              </div>
              <div className="text-[10px] text-gray-400 tracking-widest" data-id="element-1683" data-mp="2sukab0juq3lf">
                DEGREE MATCHING SERVICE
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-gray-300 text-xs" data-id="element-1684" data-mp="2we2uuvme5w8l">
            <LockIcon className="w-3.5 h-3.5 text-white" data-id="element-1685" data-mp="30fo8ij6r2eju" />
            <span data-id="element-1686" data-mp="30h91w5hbvrg4">SSL Secured</span>
          </div>
        </div>
      </header>

      {/* Main Content — Two-Column, vertically centered */}
      <section className="flex-grow flex items-center" data-id="element-1687" data-mp="2rezpfa2i1o03">
        <div className="w-full max-w-7xl mx-auto px-4 py-3 md:py-16 lg:py-20" data-id="element-1688" data-mp="2rfhveyeyrgl7">
          {/* Mobile-only eyebrow + H1 — sits ABOVE the form so the user
           reads the value prop, then immediately sees the form. */}
          <div className="lg:hidden text-center mb-5" data-id="element-1689" data-mp="3k5q8wcyk1ctg">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4 text-[11px] font-bold tracking-widest" style={{
            backgroundColor: '#4A49E5',
            color: '#ffffff'
          }} data-id="element-1690" data-mp="35e7uukp99scq">
              <span className="w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: '#ffffff'
            }} data-id="element-1691" data-mp="6padb4t6kgb3"></span>
              {template.eyebrow}
            </div>
            <h1 className="font-bold leading-[1.1] text-gray-900" style={{
            fontSize: 'clamp(1.75rem, 7vw, 2.5rem)'
          }} data-id="element-1692" data-mp="bmfv44bgh5o7">
              {template.headlineLead}{' '}
              <span style={{
              color: ORANGE
            }} data-id="element-1693" data-mp="3trj1rth2lpqq">
                {template.headlineHighlight}
              </span>{' '}
              {template.headlineTrail}
            </h1>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-10 lg:gap-16 items-center" data-id="element-1694" data-mp="3k298496kvz0i">
            {/* LEFT: Headline — appears BELOW the form on mobile, beside it on desktop */}
            <div className="text-center lg:text-left order-2 lg:order-1" data-id="element-1695" data-mp="3azzxzkf827nn">
              {/* Desktop-only eyebrow + H1 (mobile renders these above the grid) */}
              <div className="hidden lg:block" data-id="element-1696" data-mp="1ljwa7382nvns">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-5 text-[11px] font-bold tracking-widest" style={{
                backgroundColor: '#4A49E5',
                color: '#ffffff'
              }} data-id="element-1697" data-mp="1lj4gjxy1c805">
                  <span className="w-1.5 h-1.5 rounded-full" style={{
                  backgroundColor: '#ffffff'
                }} data-id="element-1698" data-mp="1zj5fh7uqp5cz"></span>
                  {template.eyebrow}
                </div>

                <h1 className="font-bold leading-[1.1] mb-5 text-gray-900" style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.25rem)'
              }} data-id="element-1699" data-mp="1btkyuvm26k6v">
                  {template.headlineLead}{' '}
                  <span style={{
                  color: ORANGE
                }} data-id="element-1700" data-mp="j3kwp4ixzl7p">
                    {template.headlineHighlight}
                  </span>{' '}
                  {template.headlineTrail}
                </h1>
              </div>

              {template.financialAid ? <div className="space-y-6 max-w-xl mx-auto lg:mx-0 text-left" data-id="element-1701" data-mp="3mnigp0k1g4uj">
                  <div className="bg-white rounded-xl p-6 border-l-4 shadow-sm" style={{
                borderColor: ORANGE
              }} data-id="element-1702" data-mp="3ilx137g4dcgn">
                    <p className="text-gray-600 font-medium mb-1" data-id="element-1703" data-mp="3rn4tx9zv9l3c">
                      {template.financialAid.grantCaption.split(template.financialAid.grantAmount)[0]}
                    </p>
                    <div className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-1" data-id="element-1704" data-mp="j5ryb7vm84m3">
                      {template.financialAid.grantAmount}
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-1705" data-mp="xt8pi9gcfvdh">
                      {template.financialAid.grantCaption.split(template.financialAid.grantAmount)[1]}
                    </p>
                  </div>

                  {template.financialAid.earningsStat && <div className="bg-gray-50 rounded-xl p-5 border border-gray-200" data-id="element-1706" data-mp="sw8le43212p0">
                      <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-2" data-id="element-1707" data-mp="f1fyuloqym8x">
                        {template.financialAid.earningsStat}
                      </p>
                      {template.financialAid.earningsDisclaimer && <p className="text-xs text-gray-400 leading-tight" data-id="element-1708" data-mp="a34nu7mpu5p4">
                          {template.financialAid.earningsDisclaimer}
                        </p>}
                    </div>}

                  {(template.financialAid.weCanHeadline || template.financialAid.exploreHeadline) && <div className="grid sm:grid-cols-2 gap-4" data-id="element-1709" data-mp="2k8p506kmpsgh">
                      {template.financialAid.weCanHeadline && <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-id="element-1710" data-mp="2t6hm9g9wva6i">
                          <h3 className="font-bold text-gray-900 mb-2" data-id="element-1711" data-mp="2y6n5q9611qdi">
                            {template.financialAid.weCanHeadline}
                          </h3>
                          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1712" data-mp="333dvjo7mw0c4">
                            {template.financialAid.weCanBody}
                          </p>
                        </div>}
                      {template.financialAid.exploreHeadline && <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-id="element-1713" data-mp="3gypnxoybhepv">
                          <h3 className="font-bold text-gray-900 mb-2" data-id="element-1714" data-mp="3ltllf4mbouad">
                            {template.financialAid.exploreHeadline}
                          </h3>
                          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1715" data-mp="1f87gxhcdqtav">
                            {template.financialAid.exploreBody}
                          </p>
                        </div>}
                    </div>}
                </div> : <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0" data-id="element-1716" data-mp="10bzu099l7drw">
                  {template.subhead}
                </p>}

              {/* Stats strip — site theme */}
            </div>

            {/* RIGHT: Form — appears ABOVE the supporting text on mobile */}
            <aside className="w-full max-w-md mx-auto lg:max-w-none lg:mx-0 order-1 lg:order-2" data-id="element-1717" data-mp="1j911q1uizrzj">
              {/* Step indicator */}
              <div className="flex items-center gap-3 mb-5 px-1" data-id="element-1718" data-mp="2goom99bv9bir">
                <div className="flex items-center gap-2" data-id="element-1719" data-mp="2bxou0hdozhml">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white" style={{
                  backgroundColor: ORANGE
                }} data-id="element-1720" data-mp="2bw5cyok7p2jr">
                    <CheckIcon className="w-4 h-4" data-id="element-1721" data-mp="acuz3pwezeu" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700" data-id="element-1722" data-mp="88vcxz18mkd">
                    Profile Complete
                  </span>
                </div>
                <div className="flex-grow h-px bg-gray-300" data-id="element-1723" data-mp="3ndzs96l1xjg2"></div>
                <div className="flex items-center gap-2" data-id="element-1724" data-mp="3rgo31jtbt0fo">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse" style={{
                  backgroundColor: NAVY,
                  boxShadow: `0 0 0 4px rgba(232,89,79,0.15)`
                }} data-id="element-1725" data-mp="3ri7ij875r7ge">
                    2
                  </div>
                  <span className="text-xs font-semibold" style={{
                  color: NAVY
                }} data-id="element-1726" data-mp="1gna384jlkil4">
                    Confirm Email
                  </span>
                </div>
              </div>

              {isSelector ? <form onSubmit={handleSelectorSubmit} className="bg-white rounded-2xl p-7 md:p-9 shadow-xl border border-gray-200" data-id="element-1727" data-mp="3ay06p13v39av">
                  <div className="text-center mb-6" data-id="element-1728" data-mp="3jvtwrub196pl">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" data-id="element-1729" data-mp="3eyakv11t560t">
                      {template.cardTitle}
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-1730" data-mp="3tusz6v57g645">
                      {template.cardSubtitle}
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-1731" data-mp="1zh0gcb12r4jh">
                    <div data-id="element-1732" data-mp="1zhtkr5ck9hyg">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1733" data-mp="1zj4pp8hqsvr5">
                        Degree of Interest <span className="text-red-500" data-id="element-1734" data-mp="ll8svmzilg47">*</span>
                      </label>
                      <select value={degreeLevel} onChange={e => setDegreeLevel(e.target.value ? Number(e.target.value) : '')} required className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1735" data-mp="lnmk1kr2q86h">
                        <option value="" data-id="element-1736" data-mp="33pi44yp3rv7l">-- Please Select --</option>
                        {degreeLevelOptions.filter(o => o.value !== 5).map(option => <option key={option.value} value={option.value} data-id="element-1737" data-mp="2tuyhvibzw5lw">
                              {option.label}
                            </option>)}
                      </select>
                    </div>

                    <div data-id="element-1738" data-mp="2g20eb8pef779">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1739" data-mp="2g2rylndjpii8">
                        Area of Study <span className="text-red-500" data-id="element-1740" data-mp="2b12r2hpj7myw">*</span>
                      </label>
                      <select value={areaOfStudy} onChange={e => {
                    const v = e.target.value ? Number(e.target.value) : '';
                    setAreaOfStudy(v);
                    setProgram('');
                  }} required className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1741" data-mp="264m07fqy6ui4">
                        <option value="" data-id="element-1742" data-mp="mgw4u0siga5t">-- Please Select --</option>
                        {areaStudyOptions.map(option => <option key={option.value} value={option.value} data-id="element-1743" data-mp="118o7pdb4bvqn">
                            {option.label}
                          </option>)}
                      </select>
                    </div>

                    <div data-id="element-1744" data-mp="3j34955d0cbl">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1745" data-mp="3jux487knvy6">
                        Program <span className="text-red-500" data-id="element-1746" data-mp="hjnkbtjw3u1e">*</span>
                      </label>
                      <select value={program} onChange={e => setProgram(e.target.value ? Number(e.target.value) : '')} required disabled={!areaOfStudy} className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1747" data-mp="dgr7kjh16s5d">
                        <option value="" data-id="element-1748" data-mp="12t9izkax2wij">
                          {areaOfStudy ? '-- Please Select --' : 'Select an area of study first'}
                        </option>
                        {availablePrograms.map(option => <option key={option.value} value={option.value} data-id="element-1749" data-mp="p2d0imapfvp7">
                            {option.label}
                          </option>)}
                      </select>
                    </div>

                    <button type="submit" disabled={submitting || !degreeLevel || !areaOfStudy || !program} className="w-full flex items-center justify-center gap-2 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed" style={{
                  backgroundColor: ORANGE
                }} onMouseEnter={e => {
                  if (!(e.currentTarget as HTMLButtonElement).disabled) {
                    ;
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
                  }
                }} onMouseLeave={e => {
                  ;
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
                }} data-id="element-1750" data-mp="1hrtn33bn8idi">
                      <span data-id="element-1751" data-mp="60vtj6t1aoh1">{submitting ? 'Loading…' : template.ctaLabel}</span>
                      <ArrowRightIcon className="w-4 h-4" data-id="element-1752" data-mp="12c6f32pf6n3" />
                    </button>

                    <p className="text-center text-xs text-gray-500 flex items-center justify-center gap-1.5" data-id="element-1753" data-mp="3s7u2o3sbfryx">
                      <LockIcon className="w-3.5 h-3.5" data-id="element-1754" data-mp="3o71ttbncvp36" />
                      Your information is secure and confidential
                    </p>
                  </div>
                </form> : isCta ? <div className="bg-white rounded-2xl p-8 md:p-10 shadow-xl border border-gray-200 text-center" data-id="element-1755" data-mp="27nyq4ow8sp0y">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1756" data-mp="2cpfrelin2di2">
                    {template.cardHeadline || template.cardTitle}
                  </h2>
                  <p className="text-base font-semibold mb-7" style={{
                color: ORANGE
              }} data-id="element-1757" data-mp="1k1a1lkcq9ybd">
                    {template.cardSubcopy || template.cardSubtitle}
                  </p>

                  <button type="button" onClick={handleCtaClick} disabled={submitting} className="inline-flex items-center justify-center gap-2 text-white font-bold py-4 px-10 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-70" style={{
                backgroundColor: ORANGE
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
              }} data-id="element-1758" data-mp="1yp4up1une4oz">
                    <span data-id="element-1759" data-mp="p7prj66wbl8y">{submitting ? 'Loading…' : template.ctaLabel}</span>
                    <ArrowRightIcon className="w-4 h-4" data-id="element-1760" data-mp="k83d4tfbzyyk" />
                  </button>

                  {template.cardFootnote && <p className="text-sm text-gray-600 mt-7 leading-relaxed max-w-sm mx-auto" data-id="element-1761" data-mp="20labdhu2rpaj">
                      {template.cardFootnote}
                    </p>}
                </div> : <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-7 md:p-9 shadow-xl border border-gray-200" data-id="element-1762" data-mp="3djbyutaxb6np">
                  <label htmlFor="bridge-email" className="block text-sm font-bold mb-3 text-gray-900" data-id="element-1763" data-mp="34l8lwle07i7i">
                    Where should we send your matches?
                  </label>

                  <div className="relative mb-4" data-id="element-1764" data-mp="15uhyvw4ra7d">
                    <MailIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-1765" data-mp="540lzw5l3bol" />
                    <input id="bridge-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="yourname@example.com" className="w-full pl-12 pr-4 py-3.5 rounded-lg border-2 border-gray-200 bg-white text-base text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors" data-id="element-1766" data-mp="3ni9subr4bse3" />
                  </div>

                  <button type="submit" disabled={submitting} className="w-full flex items-center justify-center gap-2 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-70" style={{
                backgroundColor: ORANGE
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
              }} data-id="element-1767" data-mp="2nb63oshsv3yd">
                    <span data-id="element-1768" data-mp="589a3eiel2hn">{submitting ? 'Loading…' : template.ctaLabel}</span>
                    <ArrowRightIcon className="w-4 h-4" data-id="element-1769" data-mp="a5rldbjw7tst" />
                  </button>

                  {/* Inline trust row */}
                  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-5 text-xs text-gray-600" data-id="element-1770" data-mp="f6f7pjx6fmgp">
                    <div className="flex items-center gap-1.5" data-id="element-1771" data-mp="o5afh0fvd4m4">
                      <ShieldCheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1772" data-mp="o4r09rhlsubs" />
                      <span data-id="element-1773" data-mp="t2j0r9wbdorl">No credit card</span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-1774" data-mp="xuk9wmy6qzde">
                      <ZapIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1775" data-mp="12tx6hzeuzkt6" />
                      <span data-id="element-1776" data-mp="12t3uok11m546">Instant results</span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-1777" data-mp="16wtih79w8io5">
                      <CheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1778" data-mp="1bubrsyrtozwo" />
                      <span data-id="element-1779" data-mp="1bs8udtocc4hx">No obligation</span>
                    </div>
                  </div>

                  <p className="text-[11px] text-gray-500 mt-5 leading-relaxed text-center" data-id="element-1780" data-mp="37vn8amxae9sw">
                    By clicking, you agree to our{' '}
                    <a href="/terms" className="underline text-gray-700 hover:text-gray-900" data-id="element-1781" data-mp="3hnt5qc6b5jfh">
                      Terms
                    </a>{' '}
                    &{' '}
                    <a href="/privacy-policy" className="underline text-gray-700 hover:text-gray-900" data-id="element-1782" data-mp="3rntpu0c043kq">
                      Privacy Policy
                    </a>{' '}
                    and consent to receive program info via email, phone, or
                    text.
                  </p>
                </form>}
            </aside>
          </div>
        </div>
      </section>

      {/* Content Section — How it works + Testimonials + Trust */}
      <section className="w-full bg-white border-t border-gray-200" data-id="element-1783" data-mp="grimn8lyw3qj">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-20" data-id="element-1784" data-mp="gtbvmuk1z01p">
          {/* How It Works */}
          <div className="text-center mb-12" data-id="element-1785" data-mp="lpk8206v8f56">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4 text-[11px] font-bold tracking-widest" style={{
            backgroundColor: 'rgba(232,89,79,0.1)',
            color: ORANGE
          }} data-id="element-1786" data-mp="7zgppkb3x5l0">
              <span className="w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: ORANGE
            }} data-id="element-1787" data-mp="2zklbjyhzc4x"></span>
              HOW IT WORKS
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1788" data-mp="3p83nu38cxsj3">
              Your Path to the Right Program in 3 Steps
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1789" data-mp="1jc9ecm8lpdwu">
              We've helped thousands of students find accredited online programs
              that fit their goals, schedule, and budget.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16" data-id="element-1790" data-mp="66pmvm5x5tfj">
            {[{
            icon: ClipboardListIcon,
            step: '01',
            title: 'Tell Us About You',
            body: 'Share your goals, education level, and area of interest in under a minute.'
          }, {
            icon: SparklesIcon,
            step: '02',
            title: 'Get Matched Instantly',
            body: 'Our system pairs you with accredited online programs that fit your profile.'
          }, {
            icon: SchoolIcon,
            step: '03',
            title: 'Connect With Schools',
            body: 'Compare programs side-by-side and connect directly with admissions teams.'
          }].map((s, i) => {
            const Icon = s.icon;
            return <div key={i} className="relative bg-gray-50 rounded-2xl p-7 border border-gray-200 hover:border-[#e8594f]/40 hover:shadow-md transition-all" data-id="element-1791" data-mp="1ge547z6gyhdw" data-mpi={i}>
                  <div className="flex items-center justify-between mb-5" data-id="element-1792" data-mp="1p5nksb7buz7i">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                  backgroundColor: 'rgba(232,89,79,0.1)'
                }} data-id="element-1793" data-mp="1u4qnvq3qyvcu">
                      <Icon className="w-6 h-6" style={{
                    color: ORANGE
                  }} data-id="element-1794" data-mp="11gmi74l6eia5" />
                    </div>
                    <span className="text-3xl font-bold text-gray-200" data-id="element-1795" data-mp="1bc60upaqbkgy">
                      {s.step}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1796" data-mp="2mse4yv5t73uq">
                    {s.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1797" data-mp="s501b9x8licb">
                    {s.body}
                  </p>
                </div>;
          })}
          </div>

          {/* Trust band */}
          <div className="mt-16 rounded-2xl p-8 md:p-10 relative overflow-hidden" style={{
          backgroundColor: NAVY
        }} data-id="element-1798" data-mp="ickk4etrkxsh">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1799" data-mp="8eweuuyj87w2"></div>
            <div className="relative z-10 grid md:grid-cols-3 gap-6 items-center" data-id="element-1800" data-mp="3vlgl8cawa8dl">
              <div className="flex items-start gap-3" data-id="element-1801" data-mp="4echbqa1r29e">
                <AwardIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1802" data-mp="4f5p8ukqrxt4" />
                <div data-id="element-1803" data-mp="1yrc546okz2rr">
                  <div className="font-bold text-white mb-1" data-id="element-1804" data-mp="1yrvjjdf0v911">
                    Only Accredited Schools
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1805" data-mp="23xqzk8h5w8gx">
                    Every partner school is regionally or nationally accredited.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1806" data-mp="2cxx95j9qooat">
                <ShieldCheckIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1807" data-mp="2cw2nu3uk2u67" />
                <div data-id="element-1808" data-mp="2mq2zypo98zof">
                  <div className="font-bold text-white mb-1" data-id="element-1809" data-mp="30nrivryrhwqu">
                    Your Privacy Protected
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1810" data-mp="2wlwm90rs07je">
                    SSL encrypted. We never sell your data to unrelated third
                    parties.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1811" data-mp="12a830meihit7">
                <ZapIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1812" data-mp="31dokym7of5sj" />
                <div data-id="element-1813" data-mp="3bajjv67v387c">
                  <div className="font-bold text-white mb-1" data-id="element-1814" data-mp="3ba1jbxe627tm">
                    Here When You Need Us
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1815" data-mp="3gbh37m4ly8af">
                    Explore programs and compare your options at your pace.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full border-t border-gray-200 bg-white" data-id="element-1816" data-mp="3u2cd91quevfl">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-700" data-id="element-1817" data-mp="2u80vd09ym9x">
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs mb-4" data-id="element-1818" data-mp="7uvyihj0e1d4">
            <a href="/privacy-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1819" data-mp="bvfu3gnxowx5">
              Privacy Policy
            </a>
            <a href="/terms" className="hover:text-[#e8594f] transition-colors" data-id="element-1820" data-mp="1sceof5ydq5rp">
              Terms of Use
            </a>
            <a href="/ca-notice" className="hover:text-[#e8594f] transition-colors" data-id="element-1821" data-mp="1ncihoyyas08g">
              CA Privacy Rights
            </a>
            <a href="/cookie-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1822" data-mp="2g1zh4gkc4he6">
              Cookies
            </a>
          </nav>
          <p className="text-xs text-gray-600 mb-1" data-id="element-1823" data-mp="2b4z9w2br18lh">
            © {new Date().getFullYear()} FindOnlineDegrees.org ·
            Media-Matchers LLC · All Rights Reserved.
          </p>
          <p className="text-[11px] text-gray-500 max-w-2xl mx-auto leading-relaxed" data-id="element-1824" data-mp="21ctc2bytfwyd">
            FindOnlineDegrees.org is a service that helps students explore
            accredited online degree programs. We may receive compensation from
            school partners when users inquire.
          </p>
        </div>
      </footer>
    </div>;
}